import * as stylex from "@stylexjs/stylex";
import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import "../tokens.stylex.ts";
import { color, font, fontSize, fontWeight, media, radius, space } from "../tokens.stylex.ts";
import { surface } from "./surface.styles.ts";

/** Dense, readable data table. Compose with THead/TBody/Tr/Th/Td. */
export function Table({
  children,
  ...rest
}: Omit<HTMLAttributes<HTMLTableElement>, "className" | "style">) {
  return <div {...stylex.props(surface.raised, surface.md, surface.plain, styles.frame)}>
      <div className="xw2csxc x7p5m3t x1pjcqnp">
        <table {...rest} className="xh8yej3 x1mwwwfo xluwven">
          {children}
        </table>
      </div>
    </div>;
}
export function THead({
  children
}: {
  children: ReactNode;
}) {
  return <thead className="xvccuex">{children}</thead>;
}
export function TBody({
  children
}: {
  children: ReactNode;
}) {
  return <tbody>{children}</tbody>;
}
export function Tr({
  children,
  interactive = false,
  ...rest
}: Omit<HTMLAttributes<HTMLTableRowElement>, "className" | "style"> & {
  interactive?: boolean;
}) {
  return <tr {...rest} {...{
    0: {
      className: "x178xt8z x13fuv20 x8yrp0t"
    },
    1: {
      className: "x178xt8z x13fuv20 x8yrp0t x1ypdohk x1b8pnsr x1enuzbz x1i10hfl"
    }
  }[!!interactive << 0]}>
      {children}
    </tr>;
}
export function Th({
  children,
  align = "left",
  width,
  ...rest
}: Omit<ThHTMLAttributes<HTMLTableCellElement>, "align" | "className" | "style"> & {
  align?: "left" | "right";
  width?: number | string;
}) {
  return <th scope="col" {...rest} {...stylex.props(styles.th, align === "right" && styles.right, width !== undefined && styles.width(width))}>
      {children}
    </th>;
}
export function Td({
  children,
  align = "left",
  muted = false,
  mono = false,
  ...rest
}: Omit<TdHTMLAttributes<HTMLTableCellElement>, "align" | "className" | "style"> & {
  align?: "left" | "right";
  muted?: boolean;
  mono?: boolean;
}) {
  return <td {...rest} {...{
    0: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 x9aqtqa xxymvpz xuxw1ft"
    },
    4: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 x9aqtqa xxymvpz xuxw1ft x1hr2gdg xss6m8b"
    },
    2: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 xxymvpz xuxw1ft xxe3v3u"
    },
    6: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 xxymvpz xuxw1ft x1hr2gdg xss6m8b xxe3v3u"
    },
    1: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 x9aqtqa xxymvpz xuxw1ft xxg9qwl xket8oi xjat59b"
    },
    5: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 x9aqtqa xxymvpz xuxw1ft x1hr2gdg xss6m8b xxg9qwl xket8oi xjat59b"
    },
    3: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 xxymvpz xuxw1ft xxe3v3u xxg9qwl xket8oi xjat59b"
    },
    7: {
      className: "xn3w4p2 xb4jwcr xws8vp0 xbrni73 xxymvpz xuxw1ft x1hr2gdg xss6m8b xxe3v3u xxg9qwl xket8oi xjat59b"
    }
  }[!!(align === "right") << 2 | !!muted << 1 | !!mono << 0]}>
      {children}
    </td>;
}
const styles = {
  frame: {
    kaIpWk: "xlfxrz8",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  th: {
    kZKoxP: "x90ne7k",
    kg3NbH: "xws8vp0 xbrni73",
    k9WMMc: "xdpxx8g",
    kMv6JI: "xxg9qwl",
    kGuDYH: "xmccgnw",
    k63SB2: "x14axpdx",
    kb6lSQ: "xjat59b",
    kMwMTN: "xxe3v3u",
    khDVqt: "xuxw1ft",
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x13szane",
    $$css: true
  },
  right: {
    k9WMMc: "x1hr2gdg",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  width: (width: number | string) => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }]
};